import React, { CSSProperties, memo, ReactNode } from "react";

interface SkeletonProps {
  className?: string;
  variant?: "text" | "circular" | "rectangular" | "rounded";
  width?: string | number;
  height?: string | number;
  animation?: "pulse" | "wave" | "none";
}

export const Skeleton = memo(
  ({ className = "", variant = "text", width, height, animation = "pulse" }: SkeletonProps) => {
    const variantClasses: Record<string, string> = {
      text: "rounded",
      circular: "rounded-full",
      rectangular: "rounded-none",
      rounded: "rounded-lg",
    };

    const animationClasses: Record<string, string> = {
      pulse: "animate-pulse",
      wave: "animate-pulse",
      none: "",
    };

    const style: CSSProperties = {
      width: typeof width === "number" ? `${width}px` : width,
      height: typeof height === "number" ? `${height}px` : height,
    };

    return (
      <div
        className={`bg-gray-700 ${variantClasses[variant]} ${animationClasses[animation]} ${className}`}
        style={style}
      />
    );
  }
);

Skeleton.displayName = "Skeleton";

interface DynamicSkeletonLoaderProps {
  className?: string;
  animation?: "pulse" | "wave" | "none";
  children?: ReactNode;
}

export const DynamicSkeletonLoader = memo(
  ({ className = "", animation = "pulse", children }: DynamicSkeletonLoaderProps) => {
    return (
      <div className={className}>
        {React.Children.map(children, (child) => {
          if (React.isValidElement(child) && child.type === Skeleton) {
            return React.cloneElement(child as React.ReactElement<SkeletonProps>, {
              animation: (child.props as SkeletonProps).animation || animation,
            });
          }
          return child;
        })}
      </div>
    );
  }
);

DynamicSkeletonLoader.displayName = "DynamicSkeletonLoader";
